import { useTranslation } from 'react-i18next';
import {
  AutoBalanceTransferFrequency,
  DefaultBankAccountList,
  DetailRow,
  SupportedCurrencies,
  formattedCurrency,
  getCurrencySign,
  getMaskedNumber,
} from 'vopay';

export type TransferDetails = {
  ScheduleStartDate: string;
  NameOfFrequency: AutoBalanceTransferFrequency | '';
  AutoBalanceTransferAmount: string;
  Currency: SupportedCurrencies;
  SplitByCounterparty: boolean;
};

export type TransferBankAccount = Pick<
  DefaultBankAccountList,
  | 'AccountHolderName'
  | 'AccountNumber'
  | 'ABARoutingNumber'
  | 'FinancialInstitutionNumber'
  | 'BranchTransitNumber'
  | 'Currency'
>;

/** The overview and the review show the same rows, so what is confirmed in review is what the overview renders. */
export default function useTransferDetailRows() {
  const { t } = useTranslation('translations', { keyPrefix: 'Hooks.UseTransferDetailRows' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const frequencyLabels: Record<AutoBalanceTransferFrequency, string> = {
    daily: tGlobal('FrequencyOptions.daily'),
    weekly: tGlobal('FrequencyOptions.weekly'),
    biweekly: tGlobal('FrequencyOptions.biweekly'),
    monthly: tGlobal('FrequencyOptions.monthly'),
  };

  function frequencyLabel(frequency: AutoBalanceTransferFrequency | '') {
    return (frequency && frequencyLabels[frequency]) || frequency;
  }

  function transferDetailRows(transfer: TransferDetails): DetailRow[] {
    return [
      { label: t('startDate', { defaultValue: 'Start Date' }), value: transfer.ScheduleStartDate },
      { label: t('frequency', { defaultValue: 'Frequency' }), value: frequencyLabel(transfer.NameOfFrequency) },
      {
        label: t('minimumBalance', { defaultValue: 'Minimum Balance to Trigger Transfer' }),
        value: `${getCurrencySign(transfer.Currency)}${formattedCurrency(transfer.AutoBalanceTransferAmount)}`,
      },
      { label: t('currency', { defaultValue: 'Currency' }), value: transfer.Currency },
      {
        label: t('splitByCounterparty', { defaultValue: 'Split by Counterparty' }),
        value: transfer.SplitByCounterparty ? t('on', { defaultValue: 'On' }) : t('off', { defaultValue: 'Off' }),
      },
    ];
  }

  function bankAccountDetailRows(account: TransferBankAccount): DetailRow[] {
    return [
      { label: t('name', { defaultValue: 'Name' }), value: account.AccountHolderName },
      { label: t('currency', { defaultValue: 'Currency' }), value: account.Currency },
      {
        label: t('abaRoutingNumber', { defaultValue: 'ABA Routing Number' }),
        value: account.ABARoutingNumber,
        visible: !!account.ABARoutingNumber,
      },
      {
        label: t('financialInstitutionNumber', { defaultValue: 'Financial Institution Number' }),
        value: account.FinancialInstitutionNumber,
        visible: !!account.FinancialInstitutionNumber,
      },
      {
        label: t('branchTransitNumber', { defaultValue: 'Branch Transit Number' }),
        value: account.BranchTransitNumber,
        visible: !!account.BranchTransitNumber,
      },
      {
        label: t('accountNumber', { defaultValue: 'Account Number' }),
        value: account.AccountNumber && getMaskedNumber(account.AccountNumber, '••••'),
      },
    ];
  }

  return { frequencyLabel, transferDetailRows, bankAccountDetailRows };
}
